/* eslint-disable no-console */

/* eslint-disable complexity */

/* eslint-disable react/no-unstable-nested-components */
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox } from 'src/@vymo/ui/atoms';
// eslint-disable-next-line vymo-ui/restrict-import
import { Accordion, NoData, Popup } from 'src/@vymo/ui/blocks';
import Pagination from 'src/@vymo/ui/blocks/pagination';
import Tooltip from 'src/@vymo/ui/blocks/tooltip';
import classnames from 'classnames';
import { ReactComponent as CollapsibleIcon } from 'src/assets/icons/collapsibleArrow.svg';
import { ReactComponent as SettingIcon } from 'src/assets/navigation/equalizer.svg';
import { Keys, locale } from 'src/i18n';
import Logger from 'src/logger';
import ColumnConfigurator from './columnConfigurator';
import Header from './header';
import { getCellView } from './queries';
import Row from './row';
import Search from './search';
import TableFilter from './tableFilter';
import { TableProps } from './types';
import VirtualizedRows from './virtualizedRows';
// eslint-disable-next-line import/order
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// eslint-disable-next-line import/order
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import styles from './index.module.scss';

function Table({
  tableData,
  tableConfig = { name: 'Table' },
  columnConfigs = [],
  sortOn,
  onSort,
  onRowSelect = () => {},
  classNames,
  groupFilterValues = {},
  groupGlobalFilter = '',
  expandTable = true,
  onChangePageIndex = () => {},
  onChangePageSize = () => {},
  isAccordion = true,
  isRowDisabled,
  'data-test-id': dataTestId,
  onSearch,
}: TableProps) {
  const log = React.useMemo(() => new Logger('Table'), []);
  const [globalFilter, setGlobalFilter] = useState(groupGlobalFilter);
  const [rowSelection, setRowSelection] = React.useState(
    tableConfig.selections || {},
  );
  const [filterValues, setFilterValues] = useState(groupFilterValues);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      setSorting((old) => {
        const nextSorting =
          typeof updater === 'function' ? updater(old) : updater;

        if (!_.isEqual(old, nextSorting)) {
          onSort?.(nextSorting);
        }

        return nextSorting;
      });
    },
    [onSort],
  );

  useEffect(() => {
    if (!_.isEqual(groupGlobalFilter, globalFilter)) {
      setGlobalFilter(groupGlobalFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupGlobalFilter]);

  const [columnVisibility, setColumnVisibility] = useState<any>({});

  const handleColumnVisibility = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      setColumnVisibility((old) => {
        const next =
          typeof updaterOrValue === 'function'
            ? updaterOrValue(old)
            : updaterOrValue;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (columnConfigs) {
      const columns = columnConfigs.reduce((acc, column) => {
        const key = column.field ?? column?.code;
        acc[key] = typeof column.visible === 'boolean' ? column.visible : true;
        return acc;
      }, {});
      setColumnVisibility(columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(columnConfigs)]);

  const [pinnedColumn, setPinnedColumn] = useState<string>();

  const getCheckbox = useCallback(
    (row: any) => (
      <Checkbox
        className={styles.table__body__row__checkbox}
        value={row.getIsSelected()}
        disabled={isRowDisabled?.(row.original) || !row.getCanSelect()}
        onChange={(checked, event) => {
          if (tableConfig?.singleRowSelection) {
            setRowSelection({});
          }
          row.getToggleSelectedHandler()(event);
        }}
        data-test-id={`table-row-select-${row.id}`}
      />
    ),
    [isRowDisabled, tableConfig?.singleRowSelection],
  );

  const [memoizedColumns, setMemoizedColumns] = useState<ColumnDef<any>[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  useEffect(() => {
    if (columnConfigs) {
      const tableColumns = columnConfigs?.map((column) => {
        const newColumn: any = {
          accessorKey: column.field || column?.code,
          id: column.field || column?.code,
          header: column.title,
          enableSorting: column.canSort,
          cell: (info) => {
            if (column.cell) {
              return column.cell(info.getValue(), info?.row?.original);
            }
            return getCellView(info.getValue(), column.input);
          },
          filterFn: (row, columnId, filterValue) => {
            const rowValue = row.getValue(columnId);

            // If filterValue is an array, check if any option matches rowValue
            if (Array.isArray(filterValue) && filterValue.length) {
              return filterValue.includes(rowValue);
            }

            // If filterValue is a string, check if it is included in rowValue
            if (typeof filterValue === 'string') {
              return rowValue.toString().includes(filterValue);
            }

            if (typeof filterValue === 'boolean' && filterValue) {
              return rowValue === filterValue;
            }

            // Default return true if filterValue is neither array nor string
            return true;
          },
        };
        return newColumn;
      });
      if (tableConfig?.isRowSelectionEnabled) {
        tableColumns.unshift({
          id: 'select',
          cell: ({ row }) => {
            const isDisabled = !row.getCanSelect();
            const tooltipContent = isDisabled && row.original?.message;
            return tooltipContent ? (
              <Tooltip
                placement="bottom"
                showIcon={false}
                content={tooltipContent}
              >
                {getCheckbox(row)}
              </Tooltip>
            ) : (
              getCheckbox(row)
            );
          },
        });
      }

      setMemoizedColumns(tableColumns);
      setColumnOrder(tableColumns.map((col) => col.id));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnConfigs, getCheckbox, tableConfig?.isRowSelectionEnabled]);

  const table = useReactTable({
    data: tableData,
    columns: memoizedColumns || [],
    state: {
      globalFilter,
      rowSelection,
      columnVisibility,
      sorting,
      columnOrder,
    },
    // eslint-disable-next-line no-unneeded-ternary
    manualSorting: onSort ? true : false,
    onSortingChange: handleSortingChange,
    onColumnOrderChange: setColumnOrder,
    initialState: {
      sorting: [
        {
          id: sortOn?.column || '',
          desc: sortOn?.defaultSort === 'desc',
        },
      ],
    },
    enableRowSelection: true,
    onColumnVisibilityChange: handleColumnVisibility,
    getRowId: (row) => _.get(row, tableConfig.selectionAttribute || 'id'),
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    table.setRowSelection(rowSelection);
  }, [rowSelection, table]);

  useEffect(() => {
    if (onRowSelect) {
      onRowSelect(tableConfig.name, table.getState().rowSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRowSelect, table?.getState()?.rowSelection]);

  // Apply filters to the table whenever filterValues changes
  useEffect(() => {
    const columnFilters = Object.keys(filterValues).map((columnId) => ({
      id: columnId,
      value: filterValues[columnId],
    }));
    table.setColumnFilters(columnFilters);
  }, [filterValues, table]);

  // Sorts table data on initial state
  useEffect(() => {
    if (table.getState().columnFilters[0]?.id === sortOn?.column) {
      if (table.getState().sorting[0]?.id !== sortOn?.column) {
        table.setSorting([
          { id: sortOn?.column, desc: sortOn?.defaultSort === 'desc' },
        ]);
      }
    }
  }, [sortOn, table]);

  // on select function to select all eligible rows in the table
  const onSelectAll = useCallback(
    (checked, event) => {
      event.stopPropagation();
      try {
        const rowModel = table?.getRowModel();
        const rows = rowModel?.rows;
        if (!Array.isArray(rows)) {
          log.info('Table rows are not available for selection.');
          return;
        }

        if (checked) {
          const eligibleRowIds = rows
            .filter((row) => row?.original?.eligible)
            .map((row) =>
              _.get(row?.original, tableConfig.selectionAttribute || 'id'),
            )
            .filter(Boolean);

          if (eligibleRowIds.length === 0) {
            log.info('No eligible rows to select.');
          }

          const newSelection = {};
          eligibleRowIds.forEach((id) => {
            newSelection[id] = true;
          });
          setRowSelection(newSelection);
        } else {
          setRowSelection({});
        }
      } catch (error) {
        log.info('Error during select all operation:', error);
      }
    },
    [log, table, tableConfig.selectionAttribute],
  );

  const queryClient = new QueryClient();
  if (tableConfig.hideEmpty && table.getRowModel()?.rows?.length === 0) {
    return null;
  }

  const renderTableContent = () => {
    if (!tableConfig?.pagination?.visible && tableConfig.visualizedRows) {
      return (
        <QueryClientProvider client={queryClient}>
          <Header
            table={table}
            pinnedColumn={pinnedColumn}
            columnConfiguratorEnabled={!tableConfig.hideColumnConfigurator}
          />
          <VirtualizedRows
            columns={memoizedColumns}
            fetchData={tableConfig.fetchData}
            onRowSelect={onRowSelect}
            fetchSize={tableConfig?.fetchSize}
            tableConfig={tableConfig}
          />
        </QueryClientProvider>
      );
    }

    return (
      <div className={classnames(classNames, styles.tableWrapperV2__table)}>
        <table
          data-test-id={`table-index-${
            table.getState().pagination.pageIndex + 1
          }`}
        >
          <Header
            table={table}
            pinnedColumn={pinnedColumn}
            columnConfiguratorEnabled={!tableConfig.hideColumnConfigurator}
          />
          {Boolean(table.getRowModel()?.rows?.length) && (
            <tbody>
              {table.getRowModel().rows?.map((row, index) => (
                <Row
                  key={row.original?.code ?? index}
                  row={row}
                  classNames={
                    tableConfig?.onRowClick ||
                    tableConfig?.isRowSelectionEnabled
                      ? styles.tableWrapperV2__row
                      : ''
                  }
                  onClick={(event) => {
                    if (tableConfig?.onRowClick) {
                      tableConfig?.onRowClick?.(
                        row?.getValue('id') ?? row?.id,
                        row?.original,
                      );
                    } else if (
                      tableConfig?.isRowSelectionEnabled &&
                      !(isRowDisabled?.(row.original) || !row.getCanSelect())
                    ) {
                      if (tableConfig?.singleRowSelection) {
                        setRowSelection({});
                      }
                      row.getToggleSelectedHandler()(event);
                    }
                  }}
                  pinnedColumn={pinnedColumn}
                />
              ))}
            </tbody>
          )}
        </table>
        {!tableConfig?.loading && table.getRowModel()?.rows?.length === 0 && (
          <NoData message={locale(Keys.NO_ROWS_FOUND)} />
        )}
      </div>
    );
  };

  const renderTablePage = (classes = '') => (
    <div className={styles.columnConfiguratorWrapper}>
      <div className={classnames(styles.tableWrapperV2, classes)}>
        {renderTableContent()}
        {tableConfig?.pagination?.visible && (
          <Pagination
            totalPages={
              tableConfig.pagination?.totalPages || table.getPageCount()
            }
            totalItems={tableConfig.pagination?.totalItems || tableData?.length}
            currentPage={tableConfig.pagination?.currentPage}
            pageSize={tableConfig.pagination?.pageSize}
            setParentPageSize={(pageSize) => {
              table.setPageSize(pageSize);
              onChangePageSize(pageSize);
            }}
            setParentPageIndex={(pageIndex) => {
              table.setPageIndex(pageIndex);
              onChangePageIndex(pageIndex);
            }}
          />
        )}
      </div>
      {!tableConfig?.hideColumnConfigurator && (
        <Popup
          content={
            <ColumnConfigurator
              table={table}
              pinnedColumn={pinnedColumn}
              setPinnedColumn={setPinnedColumn}
            />
          }
          placement="bottom"
          closeTrigger="outside"
        >
          <Button
            variant="subtle"
            className={styles.columnConfiguratorWrapper__button}
          >
            <SettingIcon />
          </Button>
        </Popup>
      )}
    </div>
  );

  return (
    <div className={styles.tableContainer} data-test-id={`table-${dataTestId}`}>
      {!tableConfig.hideFilters && (
        <div className={styles.table__inputControls__filterWrapper}>
          <TableFilter
            filters={tableConfig.filters || []}
            table={table}
            onChange={setFilterValues}
          />
        </div>
      )}

      {tableConfig?.showSearch && (
        <Search
          onChange={(value) => {
            if (onSearch) {
              onSearch(value);
            } else {
              setGlobalFilter(value);
            }
          }}
        />
      )}

      {isAccordion ? (
        <Accordion
          title={
            <div className={styles.table__collapsible__header}>
              {tableConfig.isRowSelectionEnabled && (
                <Checkbox
                  value={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onChange={onSelectAll}
                />
              )}
              <div className={styles.table__collapsible__title}>
                {tableConfig?.name}
              </div>
              <CollapsibleIcon />
            </div>
          }
          iconRight={<span />}
          open={expandTable}
          className={styles.table__collapsible}
        >
          {renderTablePage()}
        </Accordion>
      ) : (
        renderTablePage(styles.tableWrapperV2__nonCollapsible)
      )}
    </div>
  );
}

export default Table;
